import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import RoleGuard from '../../common/RoleGuard';
import { UserRoles } from '../../../constants/UserRoles';
import { FixedAssetRestDataProvider } from '../../../data/FixedAssetRestDataProvider';
import { SupplierInvoiceRestDataProvider } from '../../../data/SupplierInvoiceRestDataProvider';
import { SupplierRestDataProvider } from '../../../data/SupplierRestDataProvider';
import { DepreciationPeriodRestDataProvider } from '../../../data/DepreciationPeriodRestDataProvider';
import { LedgerRestDataProvider } from '../../../data/LedgerRestDataProvider';
import { FiscalYearRestDataProvider } from '../../../data/FiscalYearRestDataProvider';
import { FixedAsset } from '../../../models/FixedAsset';
import { SupplierInvoice } from '../../../models/SupplierInvoice';
import { Supplier } from '../../../models/Supplier';
import { DepreciationPeriod } from '../../../models/DepreciationPeriod';
import { Ledger } from '../../../models/Ledger';
import { LedgerType } from '../../../models/LedgerType';
import { FiscalYear } from '../../../models/FiscalYear';
import FixedAssetDialog from './FixedAssetDialog';
import './FixedAssets.scss';
import '../../../styles/GenericSiteStyles.scss';

const FixedAssets: React.FC = () => {
    const { t } = useTranslation(['common', 'screens']);

    const [assets, setAssets] = useState<FixedAsset[]>([]);
    const [supplierInvoices, setSupplierInvoices] = useState<SupplierInvoice[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [depreciationPeriods, setDepreciationPeriods] = useState<DepreciationPeriod[]>([]);
    const [costLedgers, setCostLedgers] = useState<Ledger[]>([]);
    const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [initialized, setInitialized] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState<FixedAsset | null>(null);

    const assetProvider = useMemo(() => new FixedAssetRestDataProvider(), []);
    const invoiceProvider = useMemo(() => new SupplierInvoiceRestDataProvider(), []);
    const supplierProvider = useMemo(() => new SupplierRestDataProvider(), []);
    const periodProvider = useMemo(() => new DepreciationPeriodRestDataProvider(), []);
    const ledgerProvider = useMemo(() => new LedgerRestDataProvider(), []);
    const fiscalYearProvider = useMemo(() => new FiscalYearRestDataProvider(), []);

    // Phase 1 — reference data
    useEffect(() => {
        Promise.all([invoiceProvider.getAll(), supplierProvider.getAll(), periodProvider.getAll(), ledgerProvider.getAll(), fiscalYearProvider.getAll()])
            .then(([inv, sup, per, ledgers, fy]) => {
                setSupplierInvoices(inv);
                setSuppliers(sup);
                setDepreciationPeriods(per);
                setCostLedgers(ledgers.filter(l => l.type === LedgerType.Costs));
                setFiscalYears(fy.sort((a, b) => b.start.localeCompare(a.start)));
                setInitialized(true);
            })
            .catch(e => setError(e.message));
    }, [invoiceProvider, supplierProvider, periodProvider, ledgerProvider, fiscalYearProvider]);

    // Phase 2 — main data
    const loadAssets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setAssets(await assetProvider.getAll());
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [assetProvider]);

    useEffect(() => { if (initialized) loadAssets(); }, [loadAssets, initialized]);

    const columns: DetailedListColumn<FixedAsset>[] = useMemo(() => [
        {
            key: 'name',
            header: t('screens:fixedAssets.columns.name'),
            dataField: 'name',
            type: 'string',
        },
        {
            key: 'supplier',
            header: t('screens:fixedAssets.columns.supplier'),
            dataField: 'supplierInvoiceId',
            render: (a: FixedAsset) => {
                const inv = supplierInvoices.find(i => i.id === a.supplierInvoiceId);
                const sup = suppliers.find(s => s.id === inv?.supplierId);
                return <span>{sup?.name ?? '—'}</span>;
            },
        },
        {
            key: 'depreciationPeriod',
            header: t('screens:fixedAssets.columns.depreciationPeriod'),
            dataField: 'depreciationPeriodId',
            render: (a: FixedAsset) => {
                const period = depreciationPeriods.find(p => p.id === a.depreciationPeriodId);
                return <span>{period ? `${period.name} (${period.years} ${t('screens:fixedAssets.years')})` : '—'}</span>;
            },
        },
        {
            key: 'dateOfPurchase',
            header: t('screens:fixedAssets.columns.dateOfPurchase'),
            dataField: 'dateOfPurchase',
            type: 'date',
            width: 130,
            fixedWidth: true,
        },
        {
            key: 'price',
            header: t('screens:fixedAssets.columns.price'),
            dataField: 'price',
            width: 110,
            fixedWidth: true,
            render: (a: FixedAsset) => <span>€ {a.price.toFixed(2)}</span>,
        },
        {
            key: 'businessUsePercentage',
            header: t('screens:fixedAssets.columns.businessUsePercentage'),
            dataField: 'businessUsePercentage',
            width: 80,
            fixedWidth: true,
            render: (a: FixedAsset) => <span>{a.businessUsePercentage}%</span>,
        },
        {
            key: 'remainingValue',
            header: t('screens:fixedAssets.columns.remainingValue'),
            dataField: 'remainingValue',
            width: 130,
            fixedWidth: true,
            render: (a: FixedAsset) => <span>€ {a.remainingValue.toFixed(2)}</span>,
        },
        {
            key: 'status',
            header: t('screens:fixedAssets.columns.status'),
            dataField: 'isCompletelyDepreciated',
            width: 120,
            fixedWidth: true,
            render: (a: FixedAsset) =>
                a.isCompletelyDepreciated
                    ? <span className="badge bg-secondary">{t('screens:fixedAssets.fullyDepreciated')}</span>
                    : <span className="badge bg-success">{t('screens:fixedAssets.active')}</span>,
        },
    ], [supplierInvoices, suppliers, depreciationPeriods, t]);

    const openAdd = () => { setSelectedAsset(null); setDialogOpen(true); };
    const openEdit = (a: FixedAsset) => { setSelectedAsset(a); setDialogOpen(true); };
    const closeDialog = () => setDialogOpen(false);

    const handleSave = async (asset: FixedAsset): Promise<FixedAsset> => {
        let savedId = asset.id;
        if (!asset.id) {
            savedId = crypto.randomUUID();
            await assetProvider.add({ ...asset, id: savedId });
        } else {
            await assetProvider.update(asset.id, asset);
        }
        // Refetch from the server so calculated fields are up-to-date
        const refetched = await assetProvider.getById(savedId);
        await loadAssets();
        return refetched;
    };

    const handleDelete = async (asset: FixedAsset) => {
        await assetProvider.delete(asset.id);
        closeDialog();
        await loadAssets();
    };

    const handleBook = async (asset: FixedAsset, remainderCostLedgerId: string | null) => {
        await assetProvider.book(asset.id, remainderCostLedgerId);
        // Refresh the dialog with updated booking state
        const refreshed = await assetProvider.getById(asset.id);
        setSelectedAsset(refreshed);
        await loadAssets();
    };

    const handleUnbook = async (asset: FixedAsset) => {
        await assetProvider.unbook(asset.id);
        const refreshed = await assetProvider.getById(asset.id);
        setSelectedAsset(refreshed);
        await loadAssets();
    };

    const handleBookDepreciation = async (asset: FixedAsset, fiscalYearId: string) => {
        await assetProvider.bookDepreciation(asset.id, fiscalYearId);
    };

    const handleDeleteFromList = async (asset: FixedAsset) => {
        try {
            await assetProvider.delete(asset.id);
            await loadAssets();
        } catch (e: any) {
            setError(e.message);
        }
    };

    return (
        <RoleGuard roles={`${UserRoles.Admin},${UserRoles.Manager},${UserRoles.User}`} redirectOnDeny>
            <div className="fixed-assets-page">
                <MainScreenHeader title={t('screens:fixedAssets.title')} />
                {error && <ErrorMessage message={error} />}
                <DetailedListComponent
                    dataSource={{ data: assets, setData: setAssets }}
                    columns={columns}
                    rowKey={a => a.id}
                    onAddRequest={openAdd}
                    onEditRequest={openEdit}
                    onDeleteRequest={handleDeleteFromList}
                    addRole={`${UserRoles.Admin},${UserRoles.Manager}`}
                    editRole={`${UserRoles.Admin},${UserRoles.Manager}`}
                    deleteRole={`${UserRoles.Admin},${UserRoles.Manager}`}
                />
                {loading && <div className="fixed-assets-loading">{t('common:loading')}</div>}
                <FixedAssetDialog
                    open={dialogOpen}
                    asset={selectedAsset}
                    supplierInvoices={supplierInvoices}
                    suppliers={suppliers}
                    depreciationPeriods={depreciationPeriods}
                    costLedgers={costLedgers}
                    fiscalYears={fiscalYears}
                    onSave={handleSave}
                    onDelete={handleDelete}
                    onBook={handleBook}
                    onUnbook={handleUnbook}
                    onBookDepreciation={handleBookDepreciation}
                    onClose={closeDialog}
                />
            </div>
        </RoleGuard>
    );
};

export default FixedAssets;


